
import React, { useState, useCallback, memo, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Command, CommandInput, CommandList, CommandEmpty } from "@/components/ui/command";
import CustomerList from './CustomerList';
import CustomerEmpty from './CustomerEmpty';
import CustomerLoading from './CustomerLoading';
import CustomerFooter from './CustomerFooter';
import { useCustomerStats } from './useCustomerStats';
import { Customer, CustomerSelectionOptions } from './types';
import { Search } from 'lucide-react';

interface CustomerSelectorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customers: Customer[];
  loading: boolean;
  selectedCustomerId?: string;
  onSelectCustomer: (id: string, name: string) => void;
  onCreateNew: () => void;
  options?: CustomerSelectionOptions;
}

// Utiliser memo pour éviter les rendus inutiles
const CustomerSelectorDialog = memo(({
  open,
  onOpenChange,
  customers,
  loading,
  selectedCustomerId,
  onSelectCustomer,
  onCreateNew,
  options = {}
}: CustomerSelectorDialogProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const { customersWithStats, loadingStats } = useCustomerStats(customers, open);

  console.log("CustomerSelectorDialog - open:", open, "customers:", customers.length, "customersWithStats:", customersWithStats.length);
  
  // Utiliser useCallback pour éviter de recréer cette fonction à chaque rendu
  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);
  
  // Use memo for filtered customers to prevent unnecessary recalculations
  const filteredCustomers = React.useMemo(() => {
    const customersToFilter = (customersWithStats.length > 0 ? customersWithStats : customers);
    if (!searchQuery.trim()) return customersToFilter;
    
    const query = searchQuery.toLowerCase();
    
    return customersToFilter.filter(customer => {
      // Search by name
      if (customer.name && customer.name.toLowerCase().includes(query)) {
        return true;
      }
      
      // Search by email
      if (customer.email && customer.email.toLowerCase().includes(query)) {
        return true;
      }
      
      // Search by phone
      if (customer.phone && customer.phone.includes(query)) {
        return true;
      }
      
      return false;
    });
  }, [customers, customersWithStats, searchQuery]);
  
  // Clear search query when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setSearchQuery('');
    }
  }, [open]);
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[650px] p-0 rounded-lg">
        <DialogHeader className="px-4 pt-5 pb-0">
          <DialogTitle className="text-xl font-semibold">Sélectionner un client</DialogTitle>
        </DialogHeader>
        
        <Command className="overflow-hidden rounded-t-none border-t">
          <div className="flex items-center px-3 border-b">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <CommandInput 
              placeholder="Rechercher par nom, email ou téléphone..." 
              value={searchQuery}
              onValueChange={handleSearchChange}
              className="h-11 flex-1 bg-transparent outline-none border-0 focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-muted-foreground"
            />
          </div>
          
          <CommandList className="max-h-[400px] overflow-auto p-0">
            <CommandEmpty>
              <div className="py-6 text-center text-sm">
                Aucun client ne correspond à votre recherche
              </div>
            </CommandEmpty>
            
            {loading ? (
              <CustomerLoading />
            ) : filteredCustomers.length === 0 && !searchQuery ? (
              <CustomerEmpty onCreateNew={onCreateNew} />
            ) : (
              <CustomerList 
                customers={filteredCustomers}
                selectedCustomerId={selectedCustomerId}
                onSelectCustomer={onSelectCustomer}
                loadingStats={loadingStats}
                canEdit={options.canEdit}
                onEditCustomer={options.onEditCustomer}
              />
            )}
          </CommandList>
          
          <CustomerFooter onCreateNew={onCreateNew} />
        </Command>
      </DialogContent>
    </Dialog>
  );
});

// Ajouter un displayName pour faciliter le débogage
CustomerSelectorDialog.displayName = 'CustomerSelectorDialog';

export default CustomerSelectorDialog;
